import { Injectable, TemplateRef } from '@angular/core';

import { AppinfoService, LbColumnType } from './appinfo.service';
import { TableDataPipe } from './../pipes/table-data.pipe';

import { LB } from './../../assets/TS/LBCore';

export interface TableConfig {
	rowH?: number; // row height
	footerH?: number; // footer height
	headerH?: number; // header height
	pageSize?: number;
	messages?: {
		emptyMessage?: string;
		totalMessage: string;
	};
}
export interface TableColumn {
	prop: string;
	name: string;
	minWidth?: number;
	maxWidth?: number;
	width?: number;
	sortable?: boolean;
	type?: LbColumnType;
	isPrimKey?: boolean;
	isDisplay?: boolean;
	sourceTab?: string;
	seq?: number;
	pipe?: any;
	headerClass?: string;
	cellClass?: string | Function;
	cellTemplate?: TemplateRef<any>;
	headerTemplate?: TemplateRef<any>;
	info: {tooltip?: string; fixedCellClass: string; [prop: string]: any; }; // To store extra information for the column
}
export interface TableRow {
	[rowName: string]: any;
}
export interface TableColumnOptions {
	skipGdoCheck?: boolean;
	hideColumn?: boolean;
	width?: number;
	align?: 'C' | 'L' | 'R';
	sortable?: boolean;
	cellTemplate?: TemplateRef<any>;
}

@Injectable()
export class CommonTableService {

	// Default table config
	public defaultTableConfig: TableConfig = {
		rowH: 30,
		footerH: 50,
		headerH: 30,
		pageSize: 50,
		messages: {
			emptyMessage: '',
			totalMessage: 'total'
		}
	};

	// For use of copying from table row
	private copyId = 'copyTableRowContent';

	constructor(
		private appinfoService: AppinfoService
	) { }

	setNewColumn(prop: string, name: string, options?: TableColumnOptions): TableColumn {
		options = options || {};
		const column: TableColumn = {
			prop: prop,
			name: name.toUpperCase(),
			minWidth: 20,
			pipe: new TableDataPipe(),
			// cellClass: ' ',
			info: {
				fixedCellClass: ' '
			}
		};
		// Width passed in options has higher priority than maxStringLen
		if (options.width) {
			column.width = options.width;
		}
		if (options.hideColumn) {
			column.isDisplay = false;
			this.hideColumn(column);
		}
		if (options.align) {
			column.info.fixedCellClass += ' cell-align-' + options.align;
		}
		if (options.hasOwnProperty('sortable')) {
			column.sortable = options.sortable;
		}
		if (options.cellTemplate) {
			column.cellTemplate = options.cellTemplate;
		}
		if (!options.skipGdoCheck) {
			// GDO check
			const info = this.appinfoService.columnMap[name];
			if (info) {
				column.name = info.heading;
				column.type = info.type;
				// Is column primary key or not.
				column.isPrimKey = info.isPrimeKey;

				column.isDisplay = info.isDisplay;
				// Hide columns by adding 'hide-column' and 'hide-cell' classes.
				// Don't: change prop to 'NODISPLAY'. In some cases prop might be needed.
				if (!info.isDisplay) {
					this.hideColumn(column);
				}
				column.sourceTab = info.sourceTab;
				column.type = info.type;
				column.seq = info.seq;
			} else if (prop.indexOf('.') !== -1) {
				const parts = prop.split('.');
				if (this.appinfoService.columnMap[parts[0]]) {
					column.type = this.appinfoService.columnMap[parts[0]].type;
				}
				column.name = parts[1];
				column.isDisplay = true;
			} else {
				// Display column if column doesn't have gdo
				column.isDisplay = true;
			}
		}

		column.cellClass = () => {
			return column.info.fixedCellClass;
		};
		return column;
	}
	setColumnsFromRows(rows) {
		const columns = [];
		if (rows.length) {
			// colsByProp will be used to calculate column alignment & size
			const colsByProp: {
				[prop: string]: { values: any[]; header: string; type: any; isDisplay: boolean; }
			} = {};
			rows.forEach((r) => {
				for (const prop of Object.keys(r)) {
					if (!colsByProp[prop]) {
						const propWithoutPrefix = this.getColPropWithoutPrefix(prop);
						const info = this.appinfoService.columnMap[propWithoutPrefix];
						let header = propWithoutPrefix;
						if (info && info.heading) {
							header = info.heading;
						}
						const type = info ? info.type : null;
						colsByProp[prop] = {
							values: [],
							header: header,
							type: type,
							isDisplay: info ? info.isDisplay : true
						};
					}
					colsByProp[prop].values.push(r[prop]);
				}
			});
			const numOfCols = Object.keys(colsByProp).length;
			for (const prop of Object.keys(colsByProp)) {
				const col = colsByProp[prop];
				let width;
				let align: 'C' | 'R' | 'L';
				if (col.isDisplay) {
					const display = LB.TableUtility.smartDisplay(col.values, col.header, col.type);
					if (display.size <= 4) { width = 50; }
					else if (display.size <= 20) { width = 50 + display.size * 7; }
					else if (display.size <= 50) {
						// This Is Very Rough Estimation!!! Should be improved.
						if (numOfCols <= 5) {
							width = display.size * 7;
						} else {
							width = 200;
						}
					} else {
						width = 300;
					}
					switch (display.alignment) {
						case 'center': align = 'C'; break;
						case 'left': align = 'L'; break;
						case 'right': align = 'R'; break;
					}
				}
				const column = this.setNewColumn(prop, col.header, {
					width: width,
					align: align
				});
				columns.push(column);
			}
		}
		return columns.sort(this.columnSort);
	}
	getColPropWithoutPrefix(prop): string {
		if (prop.indexOf('.') !== -1) {
			const parts = prop.split('.');
			return parts[1];
		} else {
			return prop;
		}
	}

	// setColumnsFromRow(row: TableRow, columnsMaxStringLen?: {[colName: string]: number}): TableColumn[] {
	// 	const columns: TableColumn[] = [];
	// 	if (row) {
	// 		columnsMaxStringLen = columnsMaxStringLen ? columnsMaxStringLen : {};
	// 		for (const name of Object.keys(row)) {
	// 			const column = this.setNewColumn(name, name, columnsMaxStringLen[name]);
	// 			columns.push(column);
	// 		}
	// 	}
	// 	return columns.sort(this.columnSort);
	// }

	// Get table prefix from a row
	getTablePrefix(data: TableRow): string {
		for (const col of Object.keys(data)) {
			// Index of dash in column prop
			const idxOfDash = col.indexOf('_');
			if (idxOfDash === 3) {
				return col.substring(0, 3);
			}
		}
	}
	//
	calcPageSize(containerH, footerH, headerH, rowH): number {
		let pageSize = Math.floor((containerH - footerH - headerH) / this.defaultTableConfig.rowH) - 1;
		if (!pageSize) { pageSize = 10; }
		return pageSize;
	}
	copyToClipboard(text: string) {
		if (document.getElementById(this.copyId)) {
			document.getElementById(this.copyId).remove();
		}
		const elem = document.createElement('textarea');
		elem.setAttribute('id', this.copyId);
		document.body.appendChild(elem);
		elem.value = text;
		elem.select();
	}
	hideColumn(col: TableColumn) {
		col.isDisplay = false;
		col.width = 0;
		col.minWidth = 0;
		col.maxWidth = 0;
		col.headerClass = 'hide-column';
		col.info.fixedCellClass += 'hide-cell ';
	}
	// Column sort function
	private columnSort(colA: TableColumn, colB: TableColumn) {
		if (colA.seq === undefined) {
			if (colB.seq === undefined) {
				return 0;
			} else {
				return 1;
			}
		} else if (colB.seq === undefined) {
			return -1;
		} else {
			return colA.seq > colB.seq ? 1 : -1;
		}
	}
}



// WEBPACK FOOTER //
// ./src/app/common-services/common-table.service.ts